import { useEffect, useRef, useState } from "react";

export const sellerCategoryId = (value) => String(value?._id ?? value ?? "");

const filterSignature = (filter = {}) => JSON.stringify(
  Object.keys(filter).filter((key) => filter[key] !== "" && filter[key] != null)
    .sort().map((key) => [key, String(filter[key])])
);

export const combinationChanged = (before, after) => !before
  || filterSignature(before.filter) !== filterSignature(after.filter)
  || Number(before.quantity) !== Number(after.quantity)
  || Number(before.priceFil || 0) !== Number(after.priceFil || 0);

const fromProduct = (fp) => ({
  id: String(fp._id),
  catID: sellerCategoryId(fp.catID),
  filter: { ...(fp.filter || {}) },
  quantity: fp.quantity ?? 0,
  priceFil: fp.priceFil ?? "",
  isNew: false,
});

// Keep drafts for every category. Reloading category definitions must never
// hydrate the rows again; only product data changes the persisted snapshot.
export default function useSellerCombinationRows({ productId, filterProducts, categoryId }) {
  const [state, setState] = useState({ productId, rows: [], original: [] });
  const nextId = useRef(0);

  useEffect(() => {
    const original = (filterProducts || []).map(fromProduct);
    setState((previous) => {
      if (previous.productId !== productId) return { productId, rows: original, original };
      const previousById = new Map(previous.original.map((row) => [row.id, row]));
      const draftsById = new Map(previous.rows.map((row) => [row.id, row]));
      const rows = original.flatMap((row) => {
        const before = previousById.get(row.id);
        const draft = draftsById.get(row.id);
        if (before && !draft) return []; // Pending deletion survives refresh.
        return [draft && (draft.isNew || combinationChanged(before, draft)) ? draft : row];
      });
      const remoteIds = new Set(original.map((row) => row.id));
      previous.rows.forEach((row) => {
        if (!remoteIds.has(row.id) && (row.isNew || combinationChanged(previousById.get(row.id), row))) rows.push(row);
      });
      return { productId, rows, original };
    });
  }, [productId, filterProducts]);

  const rows = state.rows.filter((row) => row.catID === categoryId);
  const originalRows = state.original.filter((row) => row.catID === categoryId);
  const patchRow = (id, patch) => setState((previous) => ({
    ...previous,
    rows: previous.rows.map((row) => row.id === id ? { ...row, ...patch } : row),
  }));
  const changeField = (id, key, value) => setState((previous) => ({
    ...previous,
    rows: previous.rows.map((row) => row.id === id ? { ...row, filter: { ...row.filter, [key]: value } } : row),
  }));
  const addRow = () => {
    const row = { id: `local-${Date.now()}-${nextId.current++}`, catID: categoryId, filter: {}, quantity: 0, priceFil: "", isNew: true };
    setState((previous) => ({ ...previous, rows: [...previous.rows, row] }));
  };
  const removeRow = (id) => setState((previous) => ({ ...previous, rows: previous.rows.filter((row) => row.id !== id) }));

  // Commit each successful request before starting the next one. Retrying a
  // partially failed save therefore never recreates already-saved variants.
  const acknowledge = (id, savedRow) => setState((previous) => ({
    ...previous,
    rows: savedRow ? previous.rows.map((row) => row.id === id ? savedRow : row) : previous.rows.filter((row) => row.id !== id),
    original: [...previous.original.filter((row) => row.id !== id), ...(savedRow ? [savedRow] : [])],
  }));

  const originalById = new Map(originalRows.map((row) => [row.id, row]));
  const currentIds = new Set(rows.map((row) => row.id));
  const changes = {
    creates: rows.filter((row) => row.isNew),
    updates: rows.filter((row) => !row.isNew && combinationChanged(originalById.get(row.id), row)),
    deletes: originalRows.filter((row) => !currentIds.has(row.id)),
  };
  return { rows, changes, patchRow, changeField, addRow, removeRow, acknowledge };
}

export function validateSellerCombinations(rows, activeDefs) {
  const duplicateRowIds = new Set();
  const incompleteRowIds = new Set();
  const signatures = new Map();
  rows.forEach((row) => {
    const quantity = Number(row.quantity);
    const price = Number(row.priceFil || 0);
    // Categories without filters still need one stock/price row. In that
    // case there are no filter values to validate; quantity and price remain
    // mandatory exactly as they are for variant rows.
    const validFilters = activeDefs.length === 0
      ? Object.keys(row.filter || {}).length === 0
      : activeDefs.every((def) => def.values.some((value) => value.value === String(row.filter[def.key] ?? "")));
    const valid = validFilters
      && row.quantity !== "" && row.quantity != null && Number.isSafeInteger(quantity) && quantity >= 0
      && Number.isFinite(price) && price >= 0;
    if (!valid) incompleteRowIds.add(row.id);
    // Include all saved keys, including legacy keys absent from today's
    // category catalog. Their values must never disappear from the payload.
    const signature = filterSignature(row.filter);
    if (signatures.has(signature)) {
      duplicateRowIds.add(row.id);
      duplicateRowIds.add(signatures.get(signature));
    } else {
      signatures.set(signature, row.id);
    }
  });
  return { duplicateRowIds, incompleteRowIds };
}
